import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';
import './course-video-player.css';

export type CourseVideoPlayerTheme = 'brand' | 'vod';
/** طرح «سینمایی» برای صفحهٔ پخش جلسه: گوشه‌های گرد و کنترل‌های شیشه‌ای */
export type CourseVideoPlayerVariant = 'default' | 'theater';

export type CourseVideoPlayerHandle = {
  getCurrentTime: () => number;
};

export type CourseVideoPlayerProps = {
  src: string;
  poster?: string;
  className?: string;
  /** در صورت نبود، نوع رسانه تشخیص داده می‌شود (گاهی کمک به خطای MEDIA_ERR_SRC_NOT_SUPPORTED است) */
  mimeType?: string;
  theme?: CourseVideoPlayerTheme;
  variant?: CourseVideoPlayerVariant;
  hardened?: boolean;
  resumeAtSec?: number | null;
  onResumeApplied?: () => void;
  onEnded?: () => void;
  onPlaybackFailed?: (persianDetail: string) => void;
  /** پس از شروعٔ موفق بارگذاری دادهٔ ویدیو (برای پاک کردن پیام خطا در والد) */
  onPlaybackReady?: () => void;
};

/**
 * key روی تک ویدیو + dispose در cleanup برای سازگاری با React StrictMode و تعویض src
 */
export const CourseVideoPlayer = forwardRef<
  CourseVideoPlayerHandle,
  CourseVideoPlayerProps
>(function CourseVideoPlayer(
  {
    src,
    poster,
    className = '',
    mimeType,
    theme = 'brand',
    variant = 'default',
    hardened = false,
    resumeAtSec = null,
    onResumeApplied,
    onEnded,
    onPlaybackFailed,
    onPlaybackReady,
  },
  ref,
) {
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const endedHandlerRef = useRef<typeof onEnded>(onEnded);
  endedHandlerRef.current = onEnded;
  const playbackFailedHandlerRef = useRef<typeof onPlaybackFailed>(
    onPlaybackFailed,
  );
  playbackFailedHandlerRef.current = onPlaybackFailed;
  const playbackReadyHandlerRef = useRef<typeof onPlaybackReady>(onPlaybackReady);
  playbackReadyHandlerRef.current = onPlaybackReady;

  const mapMediaFailure = (code: number | undefined, rawMsg?: string) => {
    const byCode: Record<number, string> = {
      1: 'بارگذاری ویدیو قطع شد.',
      2: 'خطای شبکه هنگام دریافت ویدیو (اتصال یا آدرس سرور را بررسی کنید).',
      3: 'فایل ویدیو روی سرور خوانده نشد؛ ممکن است ناقص باشد یا پسوند و نوع رسانه نامعتبر باشد.',
      4: 'مرورگر این نوع رسانه یا آدرس پخش را پشتیبانی نمی‌کند؛ فایل دیگری امتحان شود یا به‌روزرسانی نشان داده نشود.',
    };
    const base =
      typeof code === 'number' ? byCode[code] : undefined;
    const trimmed = typeof rawMsg === 'string' ? rawMsg.trim() : '';
    return [base, trimmed ? `(${trimmed})` : ''].filter(Boolean).join(' ') || byCode[4];
  };

  useImperativeHandle(ref, () => ({
    getCurrentTime: (): number => {
      const p = playerRef.current;
      if (!p) return 0;
      try {
        const ct = (p as unknown as { currentTime?: () => number }).currentTime?.();
        return typeof ct === 'number' && Number.isFinite(ct) ? ct : 0;
      } catch {
        return 0;
      }
    },
  }));

  useEffect(() => {
    const el = videoElRef.current;
    if (!src || !el) return;

    let resumeConsumed = false;
    const consumeResumeOnce = () => {
      if (resumeConsumed) return;
      resumeConsumed = true;
      if (resumeAtSec == null || resumeAtSec <= 0.35) return;
      const p = playerRef.current;
      if (!p || typeof (p as unknown as { currentTime?: (n: number) => void }).currentTime !== 'function') {
        return;
      }
      try {
        (p as unknown as { currentTime: (n: number) => void }).currentTime(resumeAtSec);
        onResumeApplied?.();
      } catch {
        /* noop */
      }
    };

    const applyTechGuards = () => {
      const tech = playerRef.current?.el()?.querySelector('video.vjs-tech') as
        | HTMLVideoElement
        | null
        | undefined;
      if (!tech) return;
      tech.setAttribute(
        'controlsList',
        hardened ? 'nodownload noremoteplayback' : 'nodownload',
      );
      tech.setAttribute('disablePictureInPicture', '');
      tech.playsInline = true;
    };

    const opts: Record<string, unknown> = {
      controls: true,
      fluid: false,
      fill: true,
      responsive: false,
      preload: 'auto',
      playsinline: true,
      inactivityTimeout: 3800,
      ...(poster ? { poster } : {}),
      controlBar: {
        pictureInPictureToggle: false,
      },
    };
    if (!hardened) {
      opts.playbackRates = [0.75, 1, 1.25, 1.5, 1.75, 2];
    }

    const player = videojs(el, opts as Parameters<typeof videojs>[1]);
    playerRef.current = player;

    let disposed = false;

    const onEndedInternal = () => endedHandlerRef.current?.();
    player.on('ended', onEndedInternal);

    const onErrorInternal = () => {
      const vErr = typeof player.error === 'function' ? player.error() : null;
      const code =
        vErr &&
        typeof (vErr as { code?: unknown }).code === 'number'
          ? (vErr as { code: number }).code
          : undefined;
      const msg =
        vErr &&
        typeof (vErr as { message?: unknown }).message === 'string'
          ? (vErr as { message: string }).message
          : '';
      playbackFailedHandlerRef.current?.(mapMediaFailure(code, msg));
    };
    player.on('error', onErrorInternal);

    const type = mimeType?.trim() || 'video/mp4';
    player.src({ src, type });

    player.ready(() => {
      applyTechGuards();
      try {
        const pAny = player as unknown as {
          trigger?: (e: string) => void;
        };
        window.requestAnimationFrame(() => {
          if (disposed) return;
          pAny.trigger?.('playerresize');
        });
      } catch {
        /* noop */
      }
    });
    player.on('loadedtechdata', () => applyTechGuards());

    const onLoadedDataOnce = () => playbackReadyHandlerRef.current?.();
    player.one('loadeddata', onLoadedDataOnce);

    player.one('loadedmetadata', () => consumeResumeOnce());

    const wrap = el.closest('.course-video-player');
    const blockCtx = (e: Event) => e.preventDefault();
    wrap?.addEventListener('contextmenu', blockCtx);

    return () => {
      disposed = true;
      wrap?.removeEventListener('contextmenu', blockCtx);
      player.off('loadeddata', onLoadedDataOnce);
      player.off('ended', onEndedInternal);
      player.off('error', onErrorInternal);
      player.dispose();
      playerRef.current = null;
    };
  }, [mimeType, onResumeApplied, poster, hardened, resumeAtSec, src]);

  if (!src) return null;

  const themeClass = theme === 'vod' ? 'course-video-player--vod' : '';
  const variantClass =
    variant === 'theater' ? 'course-video-player--theater' : '';

  return (
    <div
      className={`course-video-player vjs-rtl ${themeClass} ${variantClass} ${className}`.trim()}
      dir="rtl"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div data-vjs-player className="w-full">
        {/* key الزاماً با src تا بعد از dispose گرهٔ نو بسازد (Strict Mode) */}
        <video
          key={src}
          ref={videoElRef}
          className="video-js vjs-big-play-centered"
          playsInline
        />
      </div>
    </div>
  );
});
